import {Skeleton, Stack} from '@mui/material';
import React, {useCallback} from 'react';
import {parse, parseISO} from 'date-fns';

import {BillData, ObrigacaoPendente} from 'domain/usecases';
import {BillStatusEnum, FeaturesEnum, MyPaymentsEnum} from 'presentation/constants';
import {LatePaymentAlert} from 'presentation/components/LatePaymentAlert';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useNewBillsBemobiFeatureFlag} from 'presentation/pages/private/hooks/useNewBillsBemobiFeatureFlag';
import {translations} from 'presentation/translations-new';
import {useNavigate} from 'react-router-dom';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import CardIllustration from '../CardIllustration';

import {mapSaoLucasBillToBillData} from 'presentation/pages/private/my-payments-flow/tab-pages/open-bills/services/getSaoLucasBills/mapper';
import {useGetInvoicesQuery} from 'presentation/pages/private/my-payments-flow/tab-pages/open-bills/queries/useGetInvoicesQuery';
import {useGetOpenObligationListQuery} from 'presentation/pages/private/my-payments-flow/tab-pages/open-bills/queries/useGetOpenObligationListQuery';
import {useGetSaoLucasBillsQuery} from '../../query/useGetSaoLucasBillsQuery';
import FinancialEmptySvg from '../CardIllustration/FinancialEmptySvg';
import {FinancialCard} from '../FinancialCard';
import {FinancialCardV2} from '../FinancialCardV2';
import {LoadErrorOrEmptyAlert} from '../LoadErrorOrEmptyAlert';
import {SectionContainer} from '../SectionContainer';

const {loadError} = translations['pt-br'].commons;
const {financialsEmpty, financialsLoadError, financialsLoadErrorBemobi, viewBacklogs} =
	translations['pt-br'].privateFlow.home;
const {finances} = translations['pt-br'].privateFlow.services;

function mapObrigacaoPendenteToBillData(obrigacao: ObrigacaoPendente): BillData {
	const status = obrigacao.qtdDiasAtraso > 0 ? BillStatusEnum.EXPIRED : BillStatusEnum.OPEN;
	const vlObrigacao =
		typeof obrigacao?.vlAtualizado === 'number'
			? String(obrigacao.vlAtualizado)
			: (obrigacao?.vlAtualizado ?? obrigacao?.vlObrigacao);
	return {
		dtPrevista: obrigacao.dtPrevista,
		cdObrigacao: obrigacao.cdObrigacao,
		cdObrigacaoOrigem: obrigacao.cdObrigacaoOrigem,
		status,
		flTipoPdf: false,
		flMigracao: obrigacao.flMigracao,
		linhaDigitavel: {
			vlObrigacao,
			dtVencimento: obrigacao.dtVencimento,
			cdBarra: obrigacao.cdBarra,
		},
		cdEspecieTitulo: obrigacao.cdEspecieTitulo,
	};
}

export function Financial() {
	const navigate = useNavigate();
	const {isLaptop} = useMediaQueries();
	const {isEnabled: isNewBillsEnabled} = useNewBillsBemobiFeatureFlag();

	const {
		data: openObligationData,
		isError: isOpenObligationError,
		isLoading: isOpenObligationLoading,
	} = useGetOpenObligationListQuery(!isNewBillsEnabled);

	const {
		data: invoicesData,
		isLoading: isInvoicesLoading,
		isError: isInvoicesError,
	} = useGetInvoicesQuery(isNewBillsEnabled);

	const {beneficiary, accesses} = useAccountDataStore(store => store.accountData);
	const {credentialCodeSaoLucas, flSaoLucasSaude} = accesses;
	const {dtNascimentoUsuarioC} = beneficiary;

	const {isEnabled: isSaoLucasFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BILLS_SAO_LUCAS,
	);
	const saoLucasEnabled = isSaoLucasFlagEnabled && !!flSaoLucasSaude && !!credentialCodeSaoLucas;

	const {
		data: saoLucasBills,
		isLoading: isSaoLucasLoading,
		isError: isSaoLucasError,
	} = useGetSaoLucasBillsQuery(saoLucasEnabled, {
		userCode: credentialCodeSaoLucas ?? '',
		birthDate: dtNascimentoUsuarioC,
	});

	const handleNavigation = useCallback((route: string) => {
		navigate(route);
	}, []);

	const isError = isNewBillsEnabled ? isInvoicesError : isOpenObligationError;
	const isLoading = isNewBillsEnabled ? isInvoicesLoading : isOpenObligationLoading;
	const loadErrorDescription = isNewBillsEnabled
		? financialsLoadErrorBemobi
		: financialsLoadError;

	if (isError || (saoLucasEnabled && isSaoLucasError)) {
		return (
			<LoadErrorOrEmptyAlert
				type="error"
				title={loadError}
				description={loadErrorDescription}
			/>
		);
	}

	if (isLoading || (saoLucasEnabled && isSaoLucasLoading)) {
		return <Skeleton sx={{transform: 'initial'}} height={72} />;
	}

	const hasNoHomeBills = !openObligationData?.length || !openObligationData[0]?.linhaDigitavel;
	const hasNoInvoiceBills = !invoicesData?.obrigacoesPendentes?.length;
	const hasNoSaoLucasBills = !saoLucasBills?.length;

	let isEmpty: boolean;
	if (isNewBillsEnabled) {
		isEmpty = hasNoInvoiceBills;
	} else {
		isEmpty = saoLucasEnabled ? hasNoHomeBills && hasNoSaoLucasBills : hasNoHomeBills;
	}

	const showDelayAlert = openObligationData?.some(
		bill =>
			bill &&
			bill?.linhaDigitavel &&
			bill?.linhaDigitavel?.qtDiasAtraso &&
			bill?.linhaDigitavel?.qtDiasAtraso >= 5,
	);

	const CardComponent = isNewBillsEnabled ? FinancialCardV2 : FinancialCard;

	const oldestInvoiceBill = invoicesData?.obrigacoesPendentes?.reduce<
		ObrigacaoPendente | undefined
	>((oldest, current) => {
		if (!oldest) return current;
		const oldestDate = parse(oldest.dtVencimento, 'dd/MM/yyyy', new Date());
		const currentDate = parse(current.dtVencimento, 'dd/MM/yyyy', new Date());
		return currentDate < oldestDate ? current : oldest;
	}, undefined);

	const mappedInvoiceBill = oldestInvoiceBill
		? mapObrigacaoPendenteToBillData(oldestInvoiceBill)
		: null;

	const oldestHomeBill = openObligationData?.[0];
	const oldestSaoLucasBill = saoLucasBills?.length
		? saoLucasBills.reduce((oldest, current) => {
				const oldestDate = parseISO(oldest.vencimentoOriginal);
				const currentDate = parseISO(current.vencimentoOriginal);
				return currentDate < oldestDate ? current : oldest;
			})
		: undefined;

	const mappedSaoLucasBill = oldestSaoLucasBill
		? mapSaoLucasBillToBillData(oldestSaoLucasBill)
		: null;
	const homeBillDate = oldestHomeBill?.dtPrevista
		? parse(oldestHomeBill.dtPrevista, 'dd/MM/yyyy', new Date())
		: null;
	const saoLucasBillDate = oldestSaoLucasBill
		? parseISO(oldestSaoLucasBill.vencimentoOriginal)
		: null;

	let homeBillToShow: BillData = oldestHomeBill ?? ({} as BillData);
	if (mappedSaoLucasBill && saoLucasBillDate) {
		if (!homeBillDate || saoLucasBillDate < homeBillDate) {
			homeBillToShow = mappedSaoLucasBill;
		}
	}

	const billToShow = isNewBillsEnabled ? (mappedInvoiceBill ?? ({} as BillData)) : homeBillToShow;

	return (
		<SectionContainer
			showTitle
			title={finances}
			actionText={isEmpty ? undefined : viewBacklogs}
			titleMarginTop={0}
			titleMarginBottom={!isLaptop ? 1 : 2}
			handleAction={isEmpty ? undefined : () => handleNavigation(MyPaymentsEnum.OPEN_BILLS)}>
			{isEmpty ? (
				<CardIllustration
					title={financialsEmpty.title}
					description={financialsEmpty.description}
					illustration={<FinancialEmptySvg />}
					customColorDescription="text.primary"
				/>
			) : (
				<Stack gap={2}>
					{showDelayAlert ? <LatePaymentAlert /> : null}
					<CardComponent bill={billToShow} />
				</Stack>
			)}
		</SectionContainer>
	);
}
